import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import {
  AnimatedList,
  type RowTransition,
} from "@/design-system/Evolution/AnimatedList";
import { UpgradeChip } from "@/design-system/Evolution/UpgradeChip";
import { cn } from "@/lib/utils";
import {
  EvoInfoRows,
  PlayStyleIcon,
  useEvoRowLabel,
  useEvoRowValue,
} from "@/pages/EvolutionsHub/EvolutionsBuilder/components/evoDisplayUtils";
import {
  Evolution,
  EvolutionCategory,
} from "@/redux/evolutions/evolutionsSlice";
import { BREAKPOINTS } from "@/settings";
import {
  describeEvolution,
  isRolePlusUpgrade,
  rolePlusMark,
  upgradesForPathway,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { GenericUpgrade } from "@repo/shared/types";
import { ReactNode, useMemo, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { RollingValue } from "./RollingValue";

/** How many rows a collapsible column shows before its "See All". */
const COLLAPSED_ROWS = 2;

/**
 * The Upgrades / Requirements block.
 *
 * "full" is the DetailedEvoPage body: upgrades and requirements each in their
 * own flat card, side by side, stacking on phones. "overview" is the tighter
 * variant inside the quick-overview Popover: bare columns, requirements and
 * additional info first, since the popover has no header chips to carry them.
 */
export const EvoDetailPanel = ({
  evolution,
  category,
  pathwayPath,
  variant = "full",
  collapsible = false,
  className,
}: {
  evolution: Evolution;
  category?: EvolutionCategory;
  /** Which path per level on a pathway evolution; path 1 everywhere if absent. */
  pathwayPath?: number[];
  variant?: "full" | "overview";
  /** Phones: show two rows per column, the rest behind a "See All". */
  collapsible?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation("evolutions");
  const rowLabel = useEvoRowLabel();
  const rowValue = useEvoRowValue();
  const { playStyleUpgrades } = useMemo(
    () => describeEvolution({ evolution, pathwayPath }),
    [evolution, pathwayPath],
  );
  // Phones keep the PlayStyles in the corner and the roles as rows. Wider
  // screens give both their own row under the stats, where they are seen.
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const grantsRow = !isMobile;
  const { statUpgrades: otherUpgrades, roleUpgrades } = useMemo(
    () => keyedUpgrades(evolution, pathwayPath, grantsRow),
    [evolution, pathwayPath, grantsRow],
  );
  const isFull = variant === "full";
  // Path switches only animate on desktop; phones and tablets swap instantly.
  const animated = !useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  // Few upgrades read better as a single column in the popover.
  const twoUpgradeColumns = otherUpgrades.length > 6;

  const requirementsColumn = (
    <div>
      <SectionLabel className={cn(isFull && "mb-1.5 pb-0")}>
        {t("sections.requirements")}
      </SectionLabel>
      <Rows
        rows={evolution.requirements.map((req) => (
          <Row
            key={req.label}
            label={rowLabel(req)}
            value={rowValue(req)}
            dense={isFull}
          />
        ))}
        collapsible={collapsible}
        showAllLabel={t("detail.seeAllRequirements")}
        hideLabel={t("detail.hideRequirements")}
      />
      {/* The page's header chips already say state, matches, repeats,
          category and source; only the popover still needs the rows. */}
      {variant === "overview" && (
        <>
          <SectionLabel className="mt-6">
            {t("sections.additionalInfo")}
          </SectionLabel>
          <EvoInfoRows evolution={evolution} category={category} Row={Row} />
        </>
      )}
    </div>
  );

  const upgradesColumn = (
    <div>
      <div
        className={cn(
          "flex items-center justify-between gap-3",
          isFull ? "mb-1.5" : "mb-1 pb-2",
          // In the popover there is no title row, so its close button sits in
          // the corner — keep the PlayStyle icons clear of it.
          variant === "overview" && "pr-9",
        )}
      >
        <div className="text-base font-semibold text-foreground">
          {t("sections.upgrades")}
        </div>
        {!grantsRow && playStyleUpgrades.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1">
            {playStyleUpgrades.map((ps) => (
              <PlayStyleIcon key={`${ps.id}-${ps.isPlus}`} upgrade={ps} />
            ))}
          </div>
        )}
      </div>
      {otherUpgrades.length === 0 ? (
        <div className="py-2 text-sm text-foreground-muted">
          {playStyleUpgrades.length === 0
            ? t("detail.noUpgrades")
            : t("detail.noStatUpgrades")}
        </div>
      ) : (
        <Rows
          rows={otherUpgrades.map(({ key, upgrade }) => (
            <Row
              key={key}
              rowKey={key}
              label={rowLabel(upgrade)}
              value={
                animated ? (
                  <RollingValue value={rowValue(upgrade)} />
                ) : (
                  rowValue(upgrade)
                )
              }
              dense={isFull}
            />
          ))}
          collapsible={collapsible}
          showAllLabel={t("detail.seeAllUpgrades")}
          hideLabel={t("detail.hideUpgrades")}
          // Switching a path swaps that level's rows; animate the difference.
          transition={
            animated
              ? {
                  version: otherUpgrades
                    .map(({ key, upgrade }) => `${key}=${rowValue(upgrade)}`)
                    .join("|"),
                  resetKey: evolution.id,
                }
              : undefined
          }
          className={cn(
            "grid grid-cols-1",
            isFull
              ? // Fixed-width columns, like the level cards: label and value
                // stay close and wide screens fit more columns.
                "gap-x-6 mobile:grid-cols-[repeat(auto-fill,minmax(12rem,15rem))]"
              : "gap-x-6",
            !isFull && twoUpgradeColumns && "touch-device:grid-cols-2",
          )}
        />
      )}
      {grantsRow &&
        (playStyleUpgrades.length > 0 || roleUpgrades.length > 0) && (
          <GrantsRow
            transition={
              animated
                ? {
                    version: [
                      ...playStyleUpgrades.map(
                        (ps) => `ps:${ps.id}:${ps.isPlus}`,
                      ),
                      ...roleUpgrades.map(
                        ({ key, upgrade }) => `${key}:${upgrade.value}`,
                      ),
                    ].join("|"),
                    resetKey: evolution.id,
                  }
                : undefined
            }
          >
            {playStyleUpgrades.map((ps) => (
              <span
                key={`ps-${ps.id}-${ps.isPlus}`}
                data-row-key={`ps:${ps.id}:${ps.isPlus}`}
                className="inline-flex"
              >
                <PlayStyleIcon upgrade={ps} />
              </span>
            ))}
            {roleUpgrades.map(({ key, upgrade }) => (
              <span
                key={key}
                data-row-key={`${key}:${upgrade.value}`}
                className="inline-flex"
              >
                <UpgradeChip
                  label={rowValue(upgrade)}
                  value={rolePlusMark(upgrade.key === "rolePlusPlus")}
                  showArrow={false}
                />
              </span>
            ))}
          </GrantsRow>
        )}
    </div>
  );

  return variant === "full" ? (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 mobile:grid-cols-[1fr_250px] touch-device:grid-cols-[1fr_300px]",
        className,
      )}
    >
      <Card hideBorder hoverable={false} className="p-4">
        {upgradesColumn}
      </Card>
      {/* Phones stack the cards: upgrades last, right above the path picker. */}
      <Card hideBorder hoverable={false} className="p-4 max-mobile:-order-1">
        {requirementsColumn}
      </Card>
    </div>
  ) : (
    <div
      className={cn(
        "grid grid-cols-1 gap-8 mobile:grid-cols-[260px_1fr] mobile:gap-10",
        className,
      )}
    >
      {requirementsColumn}
      {upgradesColumn}
    </div>
  );
};

/**
 * A column's rows, optionally folded past the first few behind a toggle.
 * The toggle only appears when there is something to fold.
 */
const Rows = ({
  rows,
  collapsible,
  showAllLabel,
  hideLabel,
  transition,
  className,
}: {
  rows: ReactNode[];
  collapsible: boolean;
  showAllLabel: string;
  hideLabel: string;
  transition?: RowTransition;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  // A function, not an inline component, so the lists keep their identity.
  const list = (children: ReactNode) =>
    transition ? (
      <AnimatedList transition={transition} className={className}>
        {children}
      </AnimatedList>
    ) : (
      <div className={className}>{children}</div>
    );
  if (!collapsible || rows.length <= COLLAPSED_ROWS) {
    return list(rows);
  }
  return (
    <>
      {list(rows.slice(0, COLLAPSED_ROWS))}
      <Collapsible open={open}>{list(rows.slice(COLLAPSED_ROWS))}</Collapsible>
      <div className="mt-3">
        <Button
          variant="secondary"
          size="medium"
          className="w-full"
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? hideLabel : showAllLabel}
        </Button>
      </div>
    </>
  );
};

/**
 * The PlayStyle icons and role chips under the stats. Switching a path swaps
 * only what differs, with the same glide as the rows above.
 */
const GrantsRow = ({
  transition,
  children,
}: {
  transition?: RowTransition;
  children: ReactNode;
}) => {
  const className = "mt-3 flex flex-wrap items-center gap-1.5";
  return transition ? (
    <AnimatedList transition={transition} className={className}>
      {children}
    </AnimatedList>
  ) : (
    <div className={className}>{children}</div>
  );
};

/**
 * The stat upgrades in display order, each keyed by level and stat. Changing
 * a level's path keeps a stat both paths grant (its value rolls) and swaps
 * only the ones that differ. With `splitRoles`, granted roles come back on
 * their own for the chips row instead of among the stats.
 */
const keyedUpgrades = (
  evolution: Evolution,
  pathwayPath: number[] | undefined,
  splitRoles: boolean,
) => {
  const keyed = upgradesForPathway(evolution, pathwayPath).flatMap(
    (upgrades, level) => {
      const seen = new Map<string, number>();
      return upgrades
        .filter((u): u is GenericUpgrade => u.type !== "playstyle")
        .map((upgrade) => {
          const nth = seen.get(upgrade.key) ?? 0;
          seen.set(upgrade.key, nth + 1);
          return { key: `${level}:${upgrade.key}:${nth}`, upgrade };
        });
    },
  );
  if (!splitRoles) return { statUpgrades: keyed, roleUpgrades: [] };
  return {
    statUpgrades: keyed.filter(({ upgrade }) => !isRolePlusUpgrade(upgrade)),
    roleUpgrades: keyed.filter(({ upgrade }) => isRolePlusUpgrade(upgrade)),
  };
};

export const SectionLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "mb-1  pb-2 text-base font-semibold text-foreground",
      className,
    )}
  >
    {children}
  </div>
);

export const Row = ({
  label,
  value,
  icon,
  rowKey,
  dense = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
  /** Identity for row transitions; see useRowTransitions. */
  rowKey?: string;
  dense?: boolean;
}) => (
  <div
    data-row-key={rowKey}
    className={cn(
      "flex items-center justify-between gap-4",
      // The negative margin gives a row flash room to breathe.
      dense ? "-mx-2 rounded-lg px-2 py-1" : "py-2",
    )}
  >
    <span className="flex min-w-0 items-center gap-2 text-sm text-foreground-muted">
      {icon}
      {label}
    </span>
    <span className="text-right text-sm font-semibold text-foreground">
      {value}
    </span>
  </div>
);
